import {
  DateTimeFormatter,
  DayOfWeek,
  LocalDate,
  ZonedDateTime,
  ZoneId,
} from '@js-joda/core';
import { Locale } from '@js-joda/locale_en-us';
import '@js-joda/timezone';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import isoWeek from 'dayjs/plugin/isoWeek';
import isToday from 'dayjs/plugin/isToday';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import _ from 'lodash';

/**
 * Returns a string representation of the `LocalDate` or `ZonedDateTime` in the specified pattern.
 *
 * By default, `ZonedDateTime` instances are formatted in the timezone of the current user (even if the pattern does not include timezone).
 * Pass a `timezone` option to override this behavior.
 *
 * See the [Joda docs](https://js-joda.github.io/js-joda/manual/formatting.html#format-patterns) for a list of pattern symbols.
 * @returns {string}
 */
export const format = (
  dateArg: LocalDate | ZonedDateTime,
  pattern: string,
  options?: { locale?: Locale; timezone?: ZoneId }
): string => {
  return dateArg instanceof LocalDate
    ? __formatDate(dateArg, pattern, options)
    : __formatDateTime(dateArg, pattern, options);
};

const __formatDate = (
  date: LocalDate,
  pattern: string,
  options?: { locale?: Locale }
): string => {
  return date.format(
    DateTimeFormatter.ofPattern(pattern).withLocale(
      options?.locale || Locale.US
    )
  );
};

const __formatDateTime = (
  dateTime: ZonedDateTime,
  pattern: string,
  options?: { locale?: Locale; timezone?: ZoneId }
): string => {
  return dateTime
    .withZoneSameInstant(
      __getFriendlyZoneId(options?.timezone || ZoneId.SYSTEM)
    )
    .format(
      DateTimeFormatter.ofPattern(pattern).withLocale(
        options?.locale || Locale.US
      )
    );
};

/**
 * `ZoneId.SYSTEM` ("SYSTEM") and `ZoneId.UTC` ("Z") do not translate well to formatted dates.
 * This function converts them to a human-friendly `ZoneId` before formatting.
 * @param {ZoneId} zoneId
 * @returns {ZoneId}
 */
const __getFriendlyZoneId = (zoneId: ZoneId): ZoneId => {
  switch (zoneId) {
    case ZoneId.SYSTEM:
      return ZonedDateTime.now().zone();
    case ZoneId.UTC:
      return ZoneId.of('UTC');
    default:
      return zoneId;
  }
};

/**
 * Returns a `DateTimeFormatter` pattern of the input `LocalDate` or `ZonedDateTime` (or string representation of either)
 * with a default format of abbreviated month, unpadded day, and year; e.g., `Jan 1, 2023`.
 *
 * Specifying `includeDayOfWeek` will include the abbreviated day of the week; e.g., `Fri, Jan 1, 2023`.
 * Specifying `useFullMonthName` will use the full month name; e.g., `January 1, 2023`.
 * Specifying `padDayOfMonth` will put a `0` before a single-digit day of month; e.g., `Jan 01, 2023`.
 * Specifying `hideSameYear` will exclude the year if it's the same as the current year;
 *   e.g., `Jan 1` using the running example, if calculated in 2023.
 * Specifying `includeTime` will include the time and zone; e.g., `Jan 1, 2023 9:30 AM PT`.
 */
export const toEthenaFormat = (
  dateArg: LocalDate | ZonedDateTime | string,
  options?: {
    includeDayOfWeek?: boolean;
    useFullMonthName?: boolean;
    padDayOfMonth?: boolean;
    hideSameYear?: boolean;
    includeTime?: boolean;
    locale?: Locale;
    timezone?: ZoneId;
  }
): string => {
  const date = typeof dateArg === 'string' ? __safeParseDate(dateArg) : dateArg;
  if (_.isNil(date)) {
    return '';
  }
  const pattern = __buildDateTimeFormatterPattern(date, options);
  return format(date, pattern, options);
};

const __safeParseDate = (date: string): LocalDate | ZonedDateTime | null => {
  try {
    return LocalDate.parse(date);
  } catch {
    try {
      return ZonedDateTime.parse(date);
    } catch {
      return null;
    }
  }
};

// See the [Joda docs](https://js-joda.github.io/js-joda/manual/formatting.html#format-patterns)
// for an explanation of the pattern symbols.
const __buildDateTimeFormatterPattern = (
  date: LocalDate | ZonedDateTime,
  options?: {
    includeDayOfWeek?: boolean;
    useFullMonthName?: boolean;
    padDayOfMonth?: boolean;
    hideSameYear?: boolean;
    includeTime?: boolean;
  }
): string => {
  const dayOfWeek = options?.includeDayOfWeek ? 'eee, ' : '';
  const month = options?.useFullMonthName ? 'MMMM' : 'MMM';
  const day = options?.padDayOfMonth ? ' dd' : ' d';
  const year =
    options?.hideSameYear && date.year() === LocalDate.now().year()
      ? ''
      : ', yyyy';
  const zonedTime = options?.includeTime ? " 'at' h:mm a z" : '';
  return `${dayOfWeek}${month}${day}${year}${zonedTime}`;
};

export const getNextWeekday = (date: LocalDate): LocalDate => {
  const nextDay = date.plusDays(1);

  if (nextDay.dayOfWeek() === DayOfWeek.SATURDAY) {
    return nextDay.plusDays(2);
  } else if (nextDay.dayOfWeek() === DayOfWeek.SUNDAY) {
    return nextDay.plusDays(1);
  }

  return nextDay;
};

// dayjs Types: https://github.com/iamkun/dayjs/blob/dev/types/index.d.ts

dayjs.extend(advancedFormat);
dayjs.extend(isoWeek);
dayjs.extend(relativeTime);
dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(isToday);

/**
 * @deprecated
 * We are transitioning to Joda to allow for consistent date handling across the frontend and backend.
 *
 * - To represent dates without a time component, you should use Joda's `LocalDate`.
 * - To represent timestamps, you should use Joda's `ZonedDateTime`.
 *
 * To format dates, see the formatting helpers contained in `lib/helpers/dateHelper.ts`.
 **/
export class EthenaDate {
  private rawDate: dayjs.ConfigType;
  private dayjsDate: dayjs.Dayjs;

  constructor(date: dayjs.ConfigType = new Date(), ...rest: any[]) {
    this.rawDate = date;
    this.dayjsDate = EthenaDate.toDayjs(date, ...rest);
  }

  /* CLASS METHODS */

  private static toDayjs(
    date: dayjs.ConfigType | EthenaDate,
    ...rest: any[]
  ): dayjs.Dayjs {
    if (typeof date === 'string' || typeof date === 'number') {
      // dayjs assumes '2023-05-23' refers to midnight (local time).
      // The `Date` constructor assumes '2023-05-23' refers to midnight (UTC).
      // Our API always returns UTC dates, so we need to parse them as `Date`s before passing them to dayjs.
      return _.isEmpty(rest) ? dayjs(new Date(date)) : dayjs(date, ...rest);
    } else if (date instanceof EthenaDate) {
      return EthenaDate.toDayjs(date.rawDate, ...rest);
    } else {
      return dayjs(date, ...rest);
    }
  }

  static isDateWeekday(date: Date): boolean {
    return new EthenaDate(date).isWeekday;
  }

  static dayName(isoWeekday: number): string {
    return dayjs().isoWeekday(isoWeekday).format('dddd');
  }

  /* GETTERS */

  get isInFuture(): boolean {
    return this.dayjsDate.isAfter(dayjs());
  }

  get isInPast(): boolean {
    return this.dayjsDate.isBefore(dayjs());
  }

  get isToday(): boolean {
    return this.dayjsDate.isToday();
  }

  get isWeekday(): boolean {
    const day = this.dayjsDate.day();
    return day !== 0 && day !== 6;
  }

  /* INSTANCE METHODS */

  format(template?: string): string {
    return this.dayjsDate.format(template);
  }

  toDate(): Date {
    return this.dayjsDate.toDate();
  }

  toISOString(): string {
    return this.dayjsDate.toISOString();
  }

  add(value: number, unit?: dayjs.ManipulateType): EthenaDate {
    return new EthenaDate(this.dayjsDate.add(value, unit));
  }

  isoWeekday(value: number): EthenaDate {
    return new EthenaDate(this.dayjsDate.isoWeekday(value));
  }

  subtract(value: number, unit?: dayjs.ManipulateType): EthenaDate {
    return new EthenaDate(this.dayjsDate.subtract(value, unit));
  }

  // Server dates (not datetimes) always have UTC midnight timestamps, e.g.
  // 2022-02-19T00:00:00.000Z. Dayjs parses and displays dates in local time by
  // default, meaning calling format() and omitting timezone info would show a
  // date that is one day behind the server date (in North America). UTC mode
  // allows us to operate as if we were in the UTC TZ. This is also useful when
  // handling dates without TZ info (e.g. '2022-02-19' would be midnight
  // Feb 19, 2022 UTC).
  utc(keepLocalTime?: boolean): EthenaDate {
    return new EthenaDate(this.dayjsDate.utc(keepLocalTime));
  }

  date(): number;
  date(date: number): EthenaDate;
  date(date?: number): number | EthenaDate {
    if (_.isNumber(date)) {
      return new EthenaDate(this.dayjsDate.date(date));
    }
    return this.dayjsDate.date();
  }

  month(): number;
  month(month: number): EthenaDate;
  month(month?: number): number | EthenaDate {
    if (_.isNumber(month)) {
      return new EthenaDate(this.dayjsDate.month(month));
    }
    return this.dayjsDate.month();
  }

  year(): number;
  year(year: number): EthenaDate;
  year(year?: number): number | EthenaDate {
    if (_.isNumber(year)) {
      return new EthenaDate(this.dayjsDate.year(year));
    }
    return this.dayjsDate.year();
  }

  toJSON(): string {
    return this.dayjsDate.toISOString();
  }

  diff(
    date: dayjs.ConfigType | EthenaDate,
    unit?: dayjs.QUnitType | dayjs.OpUnitType,
    float?: boolean
  ): number {
    return this.dayjsDate.diff(EthenaDate.toDayjs(date), unit, float);
  }

  diffInDays(date: dayjs.ConfigType | EthenaDate) {
    return this.dayjsDate.isAfter(EthenaDate.toDayjs(date))
      ? -this.dayjsDate.diff(EthenaDate.toDayjs(date), 'day')
      : Math.ceil(EthenaDate.toDayjs(date).diff(this.dayjsDate, 'day', true));
  }

  nextBusinessDay(): EthenaDate {
    switch (this.dayjsDate.day()) {
      case 5:
        return new EthenaDate(this.dayjsDate.add(3, 'day'));
      case 6:
        return new EthenaDate(this.dayjsDate.add(2, 'day'));
      default:
        return new EthenaDate(this.dayjsDate.add(1, 'day'));
    }
  }

  forwardDayInclusive(day: number): EthenaDate {
    // We do not want to mutate the `dayjsDate` member as all EthenaDates are to
    // be considered immutable (to be consistent with how dayjs works).
    let dayjsDate = this.dayjsDate.clone();
    while (dayjsDate.day() != day) {
      dayjsDate = dayjsDate.add(1, 'day');
    }
    return new EthenaDate(dayjsDate);
  }

  toEthenaFormat(): string {
    const thisYear = dayjs().year();
    const dateYear = this.dayjsDate.year();
    return thisYear === dateYear
      ? this.toShortDateFormat()
      : this.toFullDateFormat();
  }

  toEthenaFormatWithDay(): string {
    return `${this.dayjsDate.format('ddd')}, ${this.toEthenaFormat()}`;
  }

  toFullDateFormat(): string {
    return this.dayjsDate.format('MMM D, YYYY');
  }

  toShortDateFormat(): string {
    return this.dayjsDate.format('MMM D');
  }

  /**
   * ISO date without a time.
   */
  toPureDateFormat(): string {
    return this.dayjsDate.format('YYYY-MM-DD');
  }

  isAfter(date: dayjs.ConfigType | EthenaDate): boolean {
    return this.dayjsDate.isAfter(EthenaDate.toDayjs(date));
  }

  isBefore(date: dayjs.ConfigType | EthenaDate): boolean {
    return this.dayjsDate.isBefore(EthenaDate.toDayjs(date));
  }

  isSame(
    date: dayjs.ConfigType | EthenaDate,
    unit?: dayjs.OpUnitType
  ): boolean {
    return this.dayjsDate.isSame(EthenaDate.toDayjs(date), unit);
  }

  /**
   * Takes a datetime, enables UTC mode, strips the time, and returns midnight
   * of stripped date in local TZ.
   */
  toLocalStartOfDate(): Date {
    return new Date(this.dayjsDate.utc().format('YYYY-MM-DD') + 'T00:00');
  }

  /**
   * Takes a datetime, strips the time, and returns midnight of stripped date in
   * UTC TZ.
   */
  toUtcStartOfDate(): Date {
    return new Date(this.dayjsDate.local().format('YYYY-MM-DD'));
  }
}

/**
 * Consider all functions below deprecated!! Use the `EthenaDate` class for all
 * date handling needs. If functionality doesn't exist, you can extend the
 * class with an appropriate method.
 */

export const convertServerDateStringToDayJsDate = (serverDate) =>
  dayjs.utc(serverDate);

export const convertServerDateToDisplayStringWithTime = (serverDate) => {
  if (!serverDate) {
    return '';
  }
  const dayJsDate = dayjs.utc(serverDate);
  // we ARE calling local cuz this is a time string and so has that fidelity
  return dayJsDate.local().format('MM/DD/YYYY hh:mma');
};
